/**
 * POI Table Types and Constants
 *
 * Shared types for the unified POI table component used across:
 * - AssetsSearchPage (full page)
 * - AutodeskViewer (sidebar)
 * - WebIfcViewer (sidebar)
 * - SitesPage (modal)
 */

// Filter condition for a single field comparison
export interface FilterCondition {
  id: string;
  field: string;
  operator: FilterOperator;
  value: string;
}

// Group of filter conditions with AND/OR logic
export interface FilterGroup {
  id: string;
  logic: 'AND' | 'OR';
  conditions: FilterCondition[];
}

// Column configuration for table display
export interface ColumnConfig {
  field: string;
  label: string;
  visible: boolean;
  width: number;
}

// Sort configuration
export interface SortConfig {
  field: string;
  direction: 'asc' | 'desc';
}

// Filter operators
export type FilterOperator =
  | 'equals'
  | 'contains'
  | 'startsWith'
  | 'endsWith'
  | 'notEquals'
  | 'isEmpty'
  | 'isNotEmpty';

// Filter field definition
export interface FilterField {
  field: string;
  label: string;
  type: 'text' | 'select';
}

// Operator definition for UI
export interface OperatorOption {
  value: FilterOperator;
  label: string;
}

// Available filter fields based on POI data structure (supports both IFC and RVT)
export const FILTER_FIELDS: FilterField[] = [
  { field: 'name', label: 'Name', type: 'text' },
  { field: 'category', label: 'Category', type: 'select' },
  { field: 'type', label: 'Type', type: 'select' },
  { field: 'subcategory', label: 'Sub-Category', type: 'select' },
  { field: 'room_name', label: 'Room Name', type: 'select' },
  { field: 'room_number', label: 'Room Number', type: 'text' },
  { field: 'source_file', label: 'Source File', type: 'select' },
  { field: 'workset', label: 'Workset', type: 'select' },
  { field: 'phase', label: 'Phase', type: 'select' },
  { field: 'level', label: 'Level', type: 'select' },
  { field: 'mark', label: 'Mark', type: 'text' },
  { field: 'system_category', label: 'System Category', type: 'select' },
  { field: 'system_subcategory', label: 'System Subcategory', type: 'select' },
  { field: 'system_name', label: 'System Name', type: 'select' },
  { field: 'element_id', label: 'Element ID', type: 'text' },
  { field: 'ifc_guid', label: 'IFC GUID', type: 'text' },
];

// Available operators
export const OPERATORS: OperatorOption[] = [
  { value: 'contains', label: 'contains' },
  { value: 'equals', label: 'equals' },
  { value: 'startsWith', label: 'starts with' },
  { value: 'endsWith', label: 'ends with' },
  { value: 'notEquals', label: 'not equals' },
  { value: 'isEmpty', label: 'is empty' },
  { value: 'isNotEmpty', label: 'is not empty' },
];

// Default column configuration - 16 columns
export const DEFAULT_COLUMNS: ColumnConfig[] = [
  { field: 'name', label: 'Name', visible: true, width: 250 },
  { field: 'category', label: 'Category', visible: false, width: 180 },
  { field: 'type', label: 'Type', visible: false, width: 180 },
  { field: 'subcategory', label: 'SubCategory', visible: false, width: 150 },
  { field: 'room_name', label: 'Room', visible: true, width: 200 },
  { field: 'room_number', label: 'Room #', visible: false, width: 100 },
  { field: 'level', label: 'Level', visible: true, width: 120 },
  { field: 'workset', label: 'Workset', visible: false, width: 120 },
  { field: 'phase', label: 'Phase', visible: false, width: 120 },
  { field: 'mark', label: 'Mark', visible: false, width: 100 },
  { field: 'source_file', label: 'Source File', visible: false, width: 200 },
  { field: 'system_category', label: 'System', visible: true, width: 150 },
  { field: 'system_subcategory', label: 'SubSystem', visible: false, width: 150 },
  { field: 'system_name', label: 'System Name', visible: false, width: 150 },
  { field: 'element_id', label: 'Element ID', visible: false, width: 120 },
  { field: 'ifc_guid', label: 'IFC GUID', visible: false, width: 280 },
];

// Pagination
export const PAGE_SIZE = 50;  // Default page size
export const PAGE_SIZE_OPTIONS = [50, 100, 200] as const;

// UnifiedPOITable component variants
export type POITableVariant = 'fullPage' | 'sidebar' | 'modal';

/**
 * One element selected in a 3D viewer, as the asset table receives it (`viewerSelectedDbIds`):
 * a bare element number, or the element with whatever identifies it. `notAsset` marks one with no
 * asset — the table names it on its "not an asset yet" line instead of shrinking to it
 * (viewers/autodesk/sceneSelection.ts).
 */
export type ViewerSelectedElement = number | {
  dbId: number;
  elementId?: number | null;
  externalId?: string | null;
  name?: string | null;
  ifcGuid?: string | null;
  sourceFile?: string | null;
  notAsset?: boolean;
  /** Its name as the model gives it, for that line, e.g. "Floor [481276]". */
  label?: string | null;
};

// Props for the unified table component
export interface UnifiedPOITableProps {
  // Variant controls layout
  variant: POITableVariant;

  // Site ID (required to load POIs)
  siteId: string;

  // Viewer integration callbacks
  onNavigateTo3D?: (poi: any) => void;
  onHighlightGroup?: (pois: any[], color?: string) => void;
  onPOIClick?: (poi: any) => void;

  // Feature flags
  showEditMode?: boolean;
  showBulkActions?: boolean;
  showColumnSelector?: boolean;

  // Model state (for 3D button enablement)
  loadedModels?: string[];
  loadingModels?: string[];

  // Request to load a model
  onRequestLoadModel?: (fileName: string) => void;

  // Permissions (from RBAC)
  canEditContent?: boolean;

  // Custom slots for additional actions
  renderExtraActions?: (selectedPOIs: any[]) => React.ReactNode;

  // Optional invalidation hook for embedded viewers that maintain their own POI caches
  onPOIsChanged?: () => void | Promise<void>;
}

// Room option for room picker
export interface RoomOption {
  id: string;
  name: string;
  level_name?: string;
}

// Systems/Locations modal result
export interface BulkOperationResult {
  updated: number;
  unchanged: number;
  failed: number;
}
